import { create } from 'zustand';
import { PlayerNotification } from '../types';

export interface Toast extends PlayerNotification {
  addedAt: number;
}

const HISTORY_KEY    = 'notif_history';
const MAX_HISTORY    = 50;
const AUTO_DISMISS   = 6000;

function loadHistory(): Toast[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? (JSON.parse(raw) as Toast[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(history: Toast[]) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history)); } catch { /* quota */ }
}

interface NotificationState {
  toasts:      Toast[];
  history:     Toast[];
  unreadCount: number;
  push:        (n: PlayerNotification) => void;
  dismiss:     (id: string) => void;
  dismissAll:  () => void;
  markAllRead: () => void;
  clearHistory:() => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  toasts:      [],
  history:     loadHistory(),
  unreadCount: 0,

  push: (n: PlayerNotification) => {
    const toast: Toast = { ...n, addedAt: Date.now() };
    const prev = get();

    // De-duplicate: skip if exact same id is already in history (offline-restore guard)
    if (prev.history.some(h => h.id === n.id)) return;

    const newHistory = [toast, ...prev.history].slice(0, MAX_HISTORY);
    saveHistory(newHistory);

    set({
      toasts:      [...prev.toasts, toast],
      history:     newHistory,
      unreadCount: prev.unreadCount + 1,
    });

    setTimeout(() => {
      if (get().toasts.find(t => t.id === n.id)) get().dismiss(n.id);
    }, AUTO_DISMISS);
  },

  dismiss: (id) =>
    set(state => ({ toasts: state.toasts.filter(t => t.id !== id) })),

  dismissAll: () => set({ toasts: [] }),

  markAllRead: () => set({ unreadCount: 0 }),

  clearHistory: () => {
    saveHistory([]);
    set({ history: [], unreadCount: 0 });
  },
}));
